import Input from "components/atoms/input/Input";
import SelectComponent from "components/atoms/select/SelectComponent";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { useState } from "react";
import * as s from "../LyveStyled";
import BigButton from "components/atoms/buttons/BigButton";
import useMutationButton, {
  MutationButtonProps,
  UseMutationButton,
} from "components/atoms/buttons/useMutationButton";
import { handleCopyClipBoard } from "utils/functions";
import * as bs from "components/atoms/buttons/ButtonStyled";
import {
  CreateGenerateKeyResponse,
  CUMULUS_TYPE,
  LicenseData,
  STORAGE_LOCATION,
} from "api/interfaces/subscription.interface";
import {
  CustomError,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import {
  cumulusPartNumberOptions,
  storageLocationOptions,
  storageWasabiLocationOptions,
} from "utils/options";
import { createGenerateKey } from "api/subscriptionAPI";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import useApiError from "hook/useApiError";
import SuccessModal from "./SuccessModal";
import Spinner from "components/atoms/loader/Spinner";
import TextArea from "components/atoms/input/TextArea";

type Props = {
  onModal: () => void;
  refetch: () => void;
  type: CUMULUS_TYPE;
};

const defaultFormData: LicenseData = {
  cpp: "",
  partNumber: "",
  location: STORAGE_LOCATION.US_CENTRAL_2,
  qty: 1,
  comment: "",
  provider: CUMULUS_TYPE.LYVE,
};

export default function AddLyveModal(props: Props) {
  const [step, setStep] = useState<number>(1);
  const [key, setKey] = useState<string>("");
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    cpp: "",
    duration: "",
    size: "",
    location: "",
  });
  const {
    handleErrorNotify,
    intlString,
    lang,
    notifyError,
    createCustomError,
  } = useApiError();
  const [formData, setFormData] = useState<LicenseData>(() => ({
    ...defaultFormData,
    provider: props.type,
    location:
      props.type === CUMULUS_TYPE.WASABI
        ? STORAGE_LOCATION.US_WEST_2
        : STORAGE_LOCATION.US_CENTRAL_2,
  }));
  const isEnableNext =
    formData.cpp && formData.partNumber && formData.location ? true : false;

  const changeFormData = (key: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };
  const createGenerateKeyButtonProps: MutationButtonProps = {
    mutationFn: () => {
      return createGenerateKey({
        payload: {
          ...formData,
          provider: props.type,
        },
      });
    },
    onClick: (mutation) => {
      if (formData.cpp.startsWith("0")) {
        notifyError(intlString(lang.label.lyve.generateKey.fail[70005]));
        return;
      }
      if (Number(formData.cpp) < 101) {
        notifyError(intlString(lang.label.lyve.generateKey.cpp.fail));
        return;
      }
      mutation.mutate();
    },
    onSuccess: (data: CreateGenerateKeyResponse) => {
      if (data.error || data.errorString) {
        const e: CustomError = createCustomError(
          data,
          lang.label.lyve.generateKey.fail.default
        );
        handleErrorNotify(
          e,
          lang.label.lyve.generateKey.fail.default,
          "label.lyve.generateKey.fail"
        );
        return;
      }
      props.refetch();
      setStep(2);
      setKey(data.result.subscriptionKeys[0]);
    },
    onError: (error) => {
      handleErrorNotify(
        error,
        lang.label.lyve.generateKey.fail.default,
        "label.lyve.generateKey.fail"
      );
    },
    disabled: !isEnableNext,
    ButtonComponent: bs.BigButton,
  };
  const createGenerateKeyButton = useMutationButton(
    createGenerateKeyButtonProps
  );
  const onCopy = () => {
    handleCopyClipBoard(key, "Subscription Key");
  };
  const onHandleClick = () => {
    setValidateMsg({
      cpp: "",
      partNumber: "",
      location: "",
    });
    if (formData.cpp === "") {
      setValidateMsg((prev) => ({
        ...prev,
        cpp: intlString(lang.label.lyve.generateKey.cpp.required),
      }));
      return;
    }
    if (Number(formData.cpp) < 100) {
      setValidateMsg((prev) => ({
        ...prev,
        cpp: intlString(lang.label.lyve.generateKey.cpp.fail),
      }));
      return;
    }
    if (formData.partNumber === "") {
      setValidateMsg((prev) => ({
        ...prev,
        partNumber: intlString(lang.label.lyve.generateKey.partNumber.required),
      }));
      return;
    }
    createGenerateKeyButton.handleClick();
  };
  return (
    <BigPopupTemplate title="Generate Key" onModal={props.onModal}>
      {step === 1 && (
        <Form
          formData={formData}
          isEnableNext={isEnableNext}
          onCancel={props.onModal}
          changeFormData={changeFormData}
          createGenerateKeyButton={createGenerateKeyButton}
          onHandleClick={onHandleClick}
          validateMsg={validateMsg}
          type={props.type}
        />
      )}

      {step === 2 && (
        <SuccessModal
          subscriptionKey={key}
          onCopy={onCopy}
          onModal={props.onModal}
        />
      )}
    </BigPopupTemplate>
  );
}

type FormProps = {
  formData: LicenseData;
  isEnableNext: boolean;
  onCancel: () => void;
  changeFormData: (key: string, value: any) => void;
  createGenerateKeyButton: UseMutationButton;
  onHandleClick: () => void;
  validateMsg: ValidateMsg;
  type: string;
};

function Form(props: FormProps) {
  const { mutation } = props.createGenerateKeyButton;
  return (
    <s.StepContainer>
      {!mutation.idle && <Spinner isCenter={true} />}
      <s.ViewLyveModalContainer>
        <ModalInput
          label="CPP Number"
          essential={true}
          isValidated={props.validateMsg.cpp ? true : false}
          validatedWord={props.validateMsg.cpp}
        >
          <Input
            placeholder="Enter CPP Number (greater than 100)"
            value={props.formData.cpp}
            onChange={(e) =>
              !isNaN(Number(e.target.value)) &&
              props.changeFormData("cpp", e.target.value)
            }
            onKeyDown={(e) => {
              if (
                isNaN(Number(e.key)) &&
                !(
                  e.key === "Backspace" ||
                  e.key === "Delete" ||
                  e.key === "Enter"
                )
              ) {
                e.preventDefault();
              }
            }}
            data-testid="subscription-lyve-add-input-is-na-n"
          />
        </ModalInput>
        <ModalInput
          label="Stock Keeping Unit (SKU)"
          essential={true}
          isValidated={props.validateMsg.partNumber ? true : false}
          validatedWord={props.validateMsg.partNumber}
        >
          <s.FormStorage>
            <SelectComponent
              options={cumulusPartNumberOptions}
              placeholder="Select Stock Keeping Unit (SKU)"
              value={(cumulusPartNumberOptions || []).find(
                (option) => option.value === props.formData.partNumber
              )}
              onChange={(value) => props.changeFormData("partNumber", value)}
              data-testid="subscription-lyve-add-selects-change-form-data"
            />
          </s.FormStorage>
        </ModalInput>
        <ModalInput
          label="Storage Location"
          essential={true}
          isBubble={false}
          bubbleContents={null}
        >
          <SelectComponent
            options={
              props.type === CUMULUS_TYPE.WASABI
                ? storageWasabiLocationOptions
                : storageLocationOptions
            }
            placeholder="Select Storage Location"
            value={(props.type === CUMULUS_TYPE.WASABI
              ? storageWasabiLocationOptions
              : storageLocationOptions || []
            ).find((option) => option.value === props.formData.location)}
            onChange={(value) => props.changeFormData("location", value)}
            data-testid="subscription-lyve-add-selects-change-form-data-2"
          />
        </ModalInput>
        <ModalInput
          label="Comment"
          essential={false}
          isBubble={false}
          bubbleContents={null}
        >
          <TextArea
            placeholder="Enter Comment"
            value={props.formData.comment}
            onChange={(e) => props.changeFormData("comment", e.target.value)}
            data-testid="subscription-lyve-add-textarea-change-form-data"
          />
        </ModalInput>
      </s.ViewLyveModalContainer>
      <s.StepButtonContainer>
        <BigButton
          label="Cancel"
          buttonType="cancel"
          onClickBtn={props.onCancel}
          data-testid="subscription-lyve-add-button-cancel"
        />

        <BigButton
          label="Generate"
          onClickBtn={props.onHandleClick}
          disabled={!mutation.idle}
          data-testid="subscription-lyve-add-button-handle"
        />
      </s.StepButtonContainer>
    </s.StepContainer>
  );
}
